import React, { useState } from 'react';
import { Minimize } from '@mui/icons-material';

import ProgressBar from '@components/input/ProgressBar';
import { useApiLoadStatuses } from '@utilities/useApiLoadStatus';
import { DataTypeLoadStatus } from '@utilities/api_load_status_store';
import { DATA_TYPE_INFO } from '@dndModels/Constants';
import CommonUtilities from '@utilities/common_utilities';
import DandDAPI from '@utilities/smart_api';

const common_util = CommonUtilities.getInstance();
const api = DandDAPI.getInstance();

function labelFor(data_type: string) {
  const info = DATA_TYPE_INFO.find(o => o.data_type === data_type);
  return info ? info.plural : data_type;
}

function statusPrefix(state: DataTypeLoadStatus["state"]) {
  if (state === "loading" || state === "preloading") {
    if (api.user && api.user.is_admin) {
      return common_util.capitalize_first(state) + " ";
    } else {
      return "Loading ";
    }
  }
  return "";
}

function statusSuffix(state: DataTypeLoadStatus["state"]) {
  if (state === "error" || state === "queued" || state === "processing" || state === "waiting") {
    return ` (${state})`;
  }
  return "";
}

function colorFor(status: DataTypeLoadStatus) {
  if (status.state === "error") {
    return "#d32f2f";
  }
  return status.priority === "priority" ? "#2196F3" : "#4CAF50";
}

function sortByPriorityThenLabel(a: DataTypeLoadStatus, b: DataTypeLoadStatus) {
  if (a.priority !== b.priority) {
    return a.priority === "priority" ? -1 : 1;
  }
  return labelFor(a.data_type).localeCompare(labelFor(b.data_type));
}

function statusKey(status: DataTypeLoadStatus) {
  return `${status.data_type}|${status.blocked}|${status.live}|${status.archive_id}|${status.user_scope}`;
}

function renderStatusBar(status: DataTypeLoadStatus) {
  return (
    <ProgressBar
        value={Math.round(status.percentage * 100)}
        max={100}
        color={colorFor(status)}
        display_progress="percent"
        horizontal
      message={`${statusPrefix(status.state)}${labelFor(status.data_type)}${statusSuffix(status.state)}`}
    />
  );
}

export function RenderDataTypeLoadStatus(status: DataTypeLoadStatus) {
  return (
    <div key={statusKey(status)} style={{ marginBottom: 6 }}>
      {renderStatusBar(status)}
    </div>
  );
}

function renderMinimizedBar(statuses: DataTypeLoadStatus[]) {
  if (statuses.length === 1) {
    return renderStatusBar(statuses[0]);
  }
  const average = statuses.reduce((sum, o) => sum + o.percentage, 0) / statuses.length;
  const color = statuses.some(o => o.state === "error") ? "#d32f2f"
    : statuses.some(o => o.priority === "priority") ? "#2196F3" : "#4CAF50";
  return (
    <ProgressBar
      value={Math.round(average * 100)}
      max={100}
      color={color}
      display_progress="percent"
      horizontal
      message={`${statuses.length} in progress`}
    />
  );
}

function renderLoadingSection(title: string, statuses: DataTypeLoadStatus[]) {
  if (statuses.length === 0) {
    return null;
  }
  return (
    <>
      <div style={{ fontWeight: "bold", marginBottom: 6, fontSize: 13 }}>
        {title} ({statuses.length})
      </div>
      {statuses.map(status => RenderDataTypeLoadStatus(status))}
    </>
  );
}

export default function ApiLoadStatusOverlay() {
  const statuses = useApiLoadStatuses();
  const [minimized, setMinimized] = useState(true);
  const active = statuses.filter(o => o.state !== "done");

  const full_active = active.filter(o => o.live).sort(sortByPriorityThenLabel);
  const blocked_active = active.filter(o => o.blocked).sort(sortByPriorityThenLabel);
  const regular_active = active.filter(o => !o.live && !o.blocked && o.archive_id === "" && !o.user_scope).sort(sortByPriorityThenLabel);
  const user_scope_active = active.filter(o => o.user_scope).sort(sortByPriorityThenLabel);

  if ((regular_active.length === 0 && user_scope_active.length === 0 && blocked_active.length === 0 && full_active.length === 0) || !api.user) {
    return null;
  }

  const container_style: React.CSSProperties = {
    position: "fixed",
    zIndex: 1300,
    left: 16,
    bottom: 16,
    minWidth: 260,
    maxWidth: 320,
    backgroundColor: "rgba(30, 30, 30, 0.92)",
    color: "white",
    borderRadius: 8,
    boxShadow: "0 2px 10px rgba(0,0,0,0.5)",
  };

  if (minimized) {
    return (
      <div
        title="Show loading details"
        onClick={() => setMinimized(false)}
        style={{ ...container_style, padding: "6px 12px", cursor: "pointer" }}
      >
        {renderMinimizedBar([...full_active, ...regular_active, ...user_scope_active, ...blocked_active])}
      </div>
    );
  }

  return (
    <div style={{
      ...container_style,
      maxHeight: "60vh",
      overflowY: "auto",
      padding: "10px 12px",
      cursor: "pointer"
    }}
    onClick={() => setMinimized(true)}>
      {renderLoadingSection("Loading Full Data", full_active)}
      {renderLoadingSection("Loading Data", regular_active)}
      {renderLoadingSection("Loading Your Source Books", user_scope_active)}
      {renderLoadingSection("Loading Blocked Data", blocked_active)}
    </div>
  );
}
